// Usage example:
// const renderer = new ProductCardRenderer({
//   showQuickView: true,
//   cardStyle: 'standard',
//   saleBadgeType: 'show_percentage'
// });
//
// const productCardHtml = renderer.render(productData, {
//   sectionId: 'collection-grid',
//   customClass: 'featured-product'
// });

class ProductCardRenderer {
  constructor(options = {}) {
    this.settings = {
      imageRatio: '1',
      showVendor: true,
      showType: true,
      showPrice: true,
      showSecondImage: true,
      showQuickView: false,
      showQuickAdd: true,
      showForm: true,
      showColorSwatches: false,
      showCompareCheckbox: true,
      cardStyle: 'standard',
      colorScheme: 'scheme-1',
      overlayColorScheme: 'scheme-1',
      contentAlignment: 'left',
      buttonStyle: 'btn--primary',
      titleLineLimit: 'none',
      saleBadgeType: 'show_percentage',
      imageSizes: `(max-width: 767px) calc((100vw - 30px) / 1),(max-width: 1023px) calc((100vw - 30px) / 3),352px`,
      imageWidths: '70, 140, 165, 355, 450, 535, 710, 900, 1070, 1420',
      mobileHideQuickAdd: false,
      chooseOptionsActions: 'open_popup',
      ...options,
    };

    this.strings = {
      availableAt:
        window.theme?.strings?.clickAndCollectStatus?.availableAt || 'In stock at {{ store_name }}',
      availableNearbyProductCard:
        window.theme?.strings?.clickAndCollectStatus?.availableNearbyProductCard ||
        'Available nearby at {{ store_name }}',
    };

    // Bind methods - only bind methods that actually exist
    this.render = this.render.bind(this);
    this.renderBadges = this.renderBadges.bind(this);
    this.renderProductInfo = this.renderProductInfo.bind(this);
    this.renderVariantForm = this.renderVariantForm.bind(this);
    this.renderStockStatus = this.renderStockStatus.bind(this);
  }

  /**
   * Main render method
   * @param {Object} product - Product data object
   * @param {Object} options - Additional rendering options
   * @returns {string} - HTML string for the product card
   */
  render(product, options = {}) {
    if (!product || !product.id) {
      throw new Error('Product object with id is required');
    }

    const renderOptions = { ...options };
    const sectionId = renderOptions.sectionId || 'product-card';
    const quickViewModalId = `QuickView-${product.id}-${sectionId}`;

    // Determine selected variant
    const selectedVariant = product.selected_or_first_available_variant || product.variants[0];
    const onSale = selectedVariant && selectedVariant.compare_at_price > selectedVariant.price;

    // Generate card wrapper classes
    const cardWrapperClasses = this.generateCardWrapperClasses(renderOptions);

    // Generate variants data attribute
    const variantsData = this.generateVariantsData(product.variants);

    return `
      <product-card
        ${renderOptions.queryID ? `data-query-id="${renderOptions.queryID}"` : ''}
        ${renderOptions.index != null ? `data-index="${renderOptions.index}"` : ''}
        data-product-id="${product.id}"
        data-object-id="${product.object_id}"
        data-variants='${this.escapeJsonForAttribute(variantsData)}'
        data-product-url="${product.url}"
        data-text-available="${this.strings.availableAt}"
        data-text-available-nearby="${this.strings.availableNearbyProductCard}"
        data-text-coming-soon="Coming Soon"
        data-text-special-order="Special order"
        class="product-card product-card-style-${this.settings.cardStyle}${
      this.settings.cardStyle === 'card' ? ` color-${this.settings.colorScheme}` : ''
    }${cardWrapperClasses}"
      >
      <script data-variants type='text/json'>
        ${JSON.stringify(this.generateVariantsData(product.variants))}
      </script>
        <div class="product-card__wrapper h-full" data-product-card-wrapper>
          ${this.renderImageWrapper(product, selectedVariant, onSale, quickViewModalId, sectionId)}
          ${this.renderProductInfo(product, selectedVariant, onSale, sectionId)}
          ${this.renderListActions(product, selectedVariant, quickViewModalId)}
        </div>
        ${this.settings.showQuickView ? this.renderQuickViewModal(product, quickViewModalId) : ''}
      </product-card>
    `;
  }

  /**
   * Render the image wrapper section
   */
  renderImageWrapper(product, selectedVariant, onSale, quickViewModalId, sectionId) {
    const featuredMedia = product.featured_image;
    const secondImage =
      this.settings.showSecondImage && product.images.length > 1 ? product.images[1] : null;
    const imageRatio = this.calculateImageRatio(featuredMedia);

    return `
      <div class="product-card__image-wrapper${
        !secondImage ? ' product-card__image-wrapper--main-only' : ''
      } color-${this.settings.overlayColorScheme} bg-none">
        <a
          href="${product.url}"
          aria-label="${this.escapeHtml(product.title)}"
          tabindex="-1"
          data-product-link
        >
          <motion-element data-motion="zoom-out-sm" class="block">
            ${this.renderMainImage(featuredMedia, imageRatio)}
            ${secondImage ? this.renderSecondImage(secondImage, imageRatio) : ''}
          </motion-element>
        </a>
        ${this.renderBadges(product, selectedVariant, onSale)}
        ${this.renderQuickViewButton(quickViewModalId)}
        ${this.renderQuickAddButton(product, selectedVariant, quickViewModalId, sectionId)}
        ${this.renderCompareCheckbox(product, sectionId)}
      </div>
    `;
  }

  /**
   * Render main product image
   */
  renderMainImage(featuredMedia, imageRatio) {
    if (!featuredMedia) {
      return `
        <div class="product-card__image product-card__image--main" style="--aspect-ratio: ${imageRatio};">
          <svg class="placeholder-svg" viewBox="0 0 525 525" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="525" height="525" fill="#F3F3F3"/>
          </svg>
        </div>
      `;
    }

    const imageUrl = this.generateImageUrl(featuredMedia.url, featuredMedia.width);
    const srcset = this.generateSrcset(featuredMedia.url, featuredMedia.width);

    return `
      <div class="media-wrapper product-card__image product-card__image--main" style="--aspect-ratio: ${imageRatio}">
        <img
          src="${imageUrl}"
          alt="${this.escapeHtml(featuredMedia.alt || '')}"
          srcset="${srcset}"
          width="${featuredMedia.width}"
          height="${featuredMedia.height}"
          loading="lazy"
          fetchpriority="low"
          class="motion-reduce"
          sizes="${this.settings.imageSizes}"
          is="image-lazy"
        />
      </div>
    `;
  }

  /**
   * Render second image (hover effect)
   */
  renderSecondImage(secondImage, imageRatio) {
    const imageUrl = this.generateImageUrl(secondImage.url, secondImage.width);
    const srcset = this.generateSrcset(secondImage.url, secondImage.width);

    return `
      <div class="media-wrapper product-card__image product-card__image--second inset-full hidden md:block" style="--aspect-ratio: ${imageRatio}">
        <img
          src="${imageUrl}"
          alt="${this.escapeHtml(secondImage.alt || '')}"
          srcset="${srcset}"
          width="${secondImage.width}"
          height="${secondImage.height}"
          loading="lazy"
          class="motion-reduce"
          sizes="${this.settings.imageSizes}"
          is="image-lazy"
        />
      </div>
    `;
  }

  /**
   * Render product badges
   */
  renderBadges(product, selectedVariant, onSale) {
    const badges = [];

    // Get badge data
    const badgeMetaobjects = window.theme?.badges || [];
    const discountAmount = product.metafields?.offers?.discount_amount;
    const discountPercent = product.metafields?.offers?.discount_percent;

    // 99B 20260220 -PO, switch to "badge_handle_list" metafield for the product, namespace passed through inconsisent between 'offers' and 'Offers'
    const productBadgeList = product.metafields?.offers?.badge_handle_list || product.metafields?.Offers?.badge_handle_list || [];

    // 99B 20260225 -PO, adding legacy product badge handle for pricing/discount badges only, as these are product-only badges
    const legacyProductBadge = product.metafields?.offers?.badge_handle;
    if (legacyProductBadge && !productBadgeList.includes(legacyProductBadge) && (legacyProductBadge.includes('discount') || legacyProductBadge.includes('sale') || legacyProductBadge.includes('price'))) {
      // additional check for duplicate discount badges
      if (!(legacyProductBadge.includes('discount') && productBadgeList.find(badge => badge?.includes('discount')))) {
        productBadgeList.push(legacyProductBadge);
      }
    }

    // 99B 20260223 -PO, Add any global variant badges where not included (note: where legacy product "badge_handle" metafield is used,
    // and no variants have "badge_handle" metafield populated, this will also push as present across all variants)
    const variantBadges = product.variants
      .map(variant => variant.metafields?.offers?.badge_handle);
    if (variantBadges[0] && variantBadges.every(badge => badge === variantBadges[0]) && !productBadgeList.includes(variantBadges[0])) {
      // additional check for duplicate discount badges
      if (!(variantBadges[0].includes('discount') && productBadgeList.find(badge => badge?.includes('discount')))) {
        productBadgeList.push(variantBadges[0]);
      }
    }

    // Check if product is in stock
    const inStock = product.available;

    // Sold out badge (if showing)
    if (window.theme.showSoldOutBadge && !inStock) {
      badges.push('<span class="f-badge f-badge--soldout" aria-hidden="true">Sold out</span>');
    }

    // 99B 20260223 -PO, loop through and add all product level badges
    for (const productBadge of productBadgeList) {
      if (!productBadge) continue;

      // 99B 20260223 -PO, hide manual pre-order/coming-soon badge if dynamic badge enabled
      if (window.theme.showComingSoonBadge && (productBadge === "pre-order" || productBadge == "coming-soon")) continue;

      const matchingBadge = badgeMetaobjects.find(badge => badge.badge_tag === productBadge);
      if (!matchingBadge) {
        console.error(`${product.title} has badge handle ${productBadge}, but cannot find badge in badge metaobjects.`);
        continue;
      }

      // Determine if this is a discount badge
      const isDiscountBadge =
        productBadge === 'discount_amt' ||
        productBadge === 'discount_perc' ||
        (productBadge && productBadge.includes('discount_'));

      // Get discount field value
      let discountField = '';
      if (productBadge === 'discount_amt') {
        discountField = discountAmount;
      } else if (
        productBadge === 'discount_perc' ||
        (productBadge && productBadge.includes('discount_'))
      ) {
        discountField = discountPercent;
      }

      if (isDiscountBadge && !discountField) {
        console.error(`${product.title} has discount badge handle ${productBadge}, but missing associated discount field.`);
        continue;
      }

      let badgeText = matchingBadge.badge_text;

      // Replace [price] placeholder for discount badges
      if (isDiscountBadge && discountField) {
        badgeText = badgeText.replace('[price]', discountField);
      }

      const badgeStyle = `background-color: ${matchingBadge.badge_background_color}; color: ${matchingBadge.badge_text_color};`;
      badges.push(
        `<span class="f-badge f-badge--${this.handleize(
          matchingBadge.badge_tag
        )}" style="${badgeStyle}" aria-hidden="true">${badgeText}</span>`
      );
    }

    // 99B 20260223 -PO, moving variant badges to be rendered here (and unhide on selected)
    if (variantBadges.some(Boolean) && !variantBadges.every(badge => badge === variantBadges[0])) {
      for (const variant of product.variants) {
        const variantBadge = variant.metafields?.offers?.badge_handle;
        if (!variantBadge) continue;
        // ignore duplicate if also in product badge list
        if (productBadgeList.includes(variantBadge)) continue;

        // 99B 20260225 -PO, hide manual pre-order/coming-soon badge if dynamic badge enabled
        if (window.theme.showComingSoonBadge && (variantBadge === "pre-order" || variantBadge == "coming-soon")) continue;

        // Skip pricing-related badges (discount, sale, price, etc.)
        const isPricingBadge = 
          variantBadge.includes('discount') ||
          variantBadge.includes('sale') ||
          variantBadge.includes('price');
        if (isPricingBadge) continue;

        const matchingBadge = badgeMetaobjects.find(badge => badge.badge_tag === variantBadge);
        if (!matchingBadge) {
          console.error(`${product.title}, variant ${variant.title}, has badge handle ${variantBadge}, but cannot find badge in badge metaobjects.`);
          continue;
        }

        // 99B 20260225 -PO, visually hide variant badge where not selected, and unhide in 99-product-card.js when variant selected
        const hideBadge = selectedVariant?.id !== variant.id;
        const badgeStyle = `${hideBadge ? "display: none; " : ""}background-color: ${matchingBadge.badge_background_color}; color: ${matchingBadge.badge_text_color};`;
        badges.push(`<span class="f-badge f-badge--${this.handleize(
          matchingBadge.badge_tag
        )}" badge-variant="${variant.id}" style="${badgeStyle}" aria-hidden="true">${matchingBadge.badge_text}</span>`);
      }
    }

    // 99B 20260225 -PO, Add dynamic coming soon badge to the product card if the setting enabled and the pre-order badge metaboject is available.
    // Note: will use the pre-order badge styling and text
    const preOrderBadge = badgeMetaobjects.find(badge => badge.badge_tag === 'pre-order');
    if (window.theme.showComingSoonBadge && preOrderBadge) {
      const preOrderBadgeStyle = `background-color: ${preOrderBadge.badge_background_color}; color: ${preOrderBadge.badge_text_color};`;
      badges.push(`<span class="f-badge f-badge--coming-soon hidden" style="${preOrderBadgeStyle}" aria-hidden="true" data-coming-soon-badge>${preOrderBadge.badge_text}</span>`)
    }

    if (badges.length > 0) {
      return `<div class="product-card__badge">${badges.join('')}</div>`;
    }

    return '';
  }

  /**
   * Convert string to handle format (lowercase, replace spaces/special chars with hyphens)
   */
  handleize(str) {
    return str
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /**
   * Render product information section
   */
  renderProductInfo(product, selectedVariant, onSale, sectionId) {
    return `
      <div class="product-card__info text-${this.settings.contentAlignment}">
        <span class="product-card__vendor-wrapper">
          ${this.renderVendor(product)}
          ${this.renderRatingSnippet(product)}
        </span>
        ${this.renderProductType(product)}
        ${this.renderTitle(product)}
        ${this.renderPrice(product, selectedVariant, onSale)}
        ${this.renderVariantForm(product, sectionId)}
        ${this.renderStockStatus(product)}
      </div>
    `;
  }

  /**
   * Render vendor information
   */
  renderVendor(product) {
    if (!this.settings.showVendor || !product.vendor) return '';

    const vendor = encodeURIComponent(product.vendor).replace(/%20/g, '+');
    const vendorLinkUrlConfig =
      window.theme?.productCardVendorLinkUrl || '/collections/all?filter.v.vendor=';
    const vendorLinkUrl = `${vendorLinkUrlConfig}${vendor}`;

    return `
      <p class="product-card__vendor text-sm m-0">
        <a
          href="${vendorLinkUrl}"
          class="reversed-link"
          aria-label="Vendor:${this.escapeHtml(product.vendor)}"
        >
          ${this.escapeHtml(product.vendor)}
        </a>
        </p>
    `;
  }

  renderRatingSnippet(product) {
    return `
      <div class="ruk_rating_snippet_card" data-sku="${product.sku}"></div>
    `;
  }

  /**
   * Render product type
   */
  renderProductType(product) {
    if (!this.settings.showType || !product.type) return '';

    return `
      <p class="product-card__type text-sm m-0">
        <a
          href="/collections/types?q=${encodeURIComponent(product.type)}"
          class="reversed-link"
          aria-label="Product type:${this.escapeHtml(product.type)}"
        >
          ${this.escapeHtml(product.type)}
        </a>
      </p>
    `;
  }

  /**
   * Render product title
   */
  renderTitle(product) {
    const lineLimitClass =
      this.settings.titleLineLimit !== 'none' ? ` text-limit-${this.settings.titleLineLimit}` : '';

    return `
      <h3 class="product-card__title text-pcard-title">
        <a
          class="reversed-link block${lineLimitClass}"
          href="${product.url}"
          data-product-link
        >
          <span class="reversed-link__text" data-product-card-inner-title>${this.escapeHtml(
            product.title
          )}</span>
        </a>
        <div class="product-card__rating-snippet mobile" data-product-card-rating>
          ${this.renderRatingSnippet(product)}
        </div>
      </h3>
    `;
  }

  /**
   * Render product price
   */
  renderPrice(product, selectedVariant, onSale) {
    if (!this.settings.showPrice) return '';

    const priceClasses = `f-price f-price--${this.settings.contentAlignment}${
      onSale ? ' f-price--on-sale' : ''
    }`;

    let priceHtml = `<div class="${priceClasses}" data-product-price>`;

    if (onSale) {
      // Sale price structure
      priceHtml += `
        <div class="f-price__regular">
          <span class="visually-hidden visually-hidden--inline">Regular price</span>
          <span class="f-price-item f-price-item--regular">
            From ${this.formatMoney(selectedVariant.price)}
          </span>
          <span class="f-price__label f-price__label--full">Full price</span>
        </div>
        <div class="f-price__sale">
          <div class="f-price__price-wrapper">
            <span class="visually-hidden visually-hidden--inline">Sale price</span>
            <span class="f-price-item f-price-item--sale">
              ${this.formatMoney(selectedVariant.price)}
            </span>
            <span class="f-price__label f-price__label--club">Club price</span>
          </div>
          <div class="f-price__price-wrapper f-price__price-wrapper--full">
            <span class="visually-hidden visually-hidden--inline">Regular price</span>
            <span class="f-price-item f-price-item--regular">
              ${this.formatMoney(selectedVariant.compare_at_price)}
            </span>
            <span class="f-price__label f-price__label--full">Full price</span>
          </div>
        </div>
      `;
    } else {
      // Regular price structure
      priceHtml += `
        <div class="f-price__regular">
          <span class="visually-hidden visually-hidden--inline">Regular price</span>
          <span class="f-price-item f-price-item--regular">
            From ${this.formatMoney(selectedVariant.price)}
          </span>
        </div>
      `;
    }

    priceHtml += `
      <div class="f-price__unit-wrapper hidden">
        <span class="visually-hidden">Unit price</span>
        <div class="f-price__unit">
          <span data-unit-price=""></span>
          <span aria-hidden="true">/</span>
          <span class="visually-hidden">per&nbsp;</span>
          <span data-unit-price-base-unit=""></span>
        </div>
      </div>
    </div>`;

    return priceHtml;
  }

  /**
   * Render variant form with buttons
   */
  renderVariantForm(product, sectionId) {
    if (!this.settings.showForm) return '';

    const formId = `product-form-info-${sectionId}${product.id}`;
    const variantsJson = JSON.stringify(product.variants);

    let formHtml = `
      <form
        method="post"
        action="/cart/add"
        id="${formId}"
        accept-charset="UTF-8"
        class="product-card__form"
        enctype="multipart/form-data"
        novalidate="novalidate"
        data-type="add-to-cart-form"
        is="product-form"
        data-product-id="${product.id}"
        data-form-id="${formId}"
        data-variants-json='${this.escapeJsonForAttribute(variantsJson)}'
      >
        <input type="hidden" name="form_type" value="product">
        <input type="hidden" name="utf8" value="✓">
    `;

    // Variant selector
    if (product.variants.length > 0 && !product.has_only_default_variant) {
      formHtml += this.renderVariantSelector(product, sectionId);
    } else {
      formHtml += `<input type="hidden" name="id" value="${product.variants[0].id}">`;
    }

    // Add to cart button
    formHtml += `
        <div class="twp-product-form-actions product-card__form-actions">
          <button
            type="submit"
            name="add"
            class="product-card__submit btn btn--primary w-full"
            ${!product.variants[0].available ? 'disabled' : ''}
          >
            <span class="btn__text">
              ${product.variants[0].available ? 'Add to cart' : 'Sold out'}
            </span>
            ${this.renderLoadingSpinner()}
          </button>
        </div>
        <input type="hidden" name="product-id" value="${product.id}">
        <input type="hidden" name="section-id" value="${sectionId}">
      </form>
    `;

    return formHtml;
  }

  /**
   * Render variant selector buttons
   */
  renderVariantSelector(product, sectionId) {
    const optionName = product.options && product.options[0] ? product.options[0].name : 'Variant';

    let selectorHtml = `
      <div class="twp-plp-variant-selector product-card__variant-selector variant-buttons">
        <span class="variant-buttons__label visually-hidden">${optionName}</span>
        <div class="variant-buttons__list no-js-hidden">
          <legend class="visually-hidden">Variant options</legend>
    `;

    product.variants.forEach(variant => {
      const variantId = `variant-${sectionId}-${product.id}-${variant.id}`;

      // Extract metafields data
      const minHeight =
        variant.metafields?.sizing_guide?.min_height ||
        variant.metafields?.search?.search_information?.minimum_rider_height ||
        '';
      const maxHeight =
        variant.metafields?.sizing_guide?.maximum_rider_height ||
        variant.metafields?.search?.search_information?.maximum_rider_height ||
        '';

      selectorHtml += `
        <div class="variant-buttons__item"${minHeight ? ` data-min-height="${minHeight}"` : ''}${
        maxHeight ? ` data-max-height="${maxHeight}"` : ''
      } data-variant-button>
          <input
            type="radio"
            id="${variantId}"
            name="id"
            value="${variant.id}"
            class="visually-hidden"
            data-variant-available="${variant.available}"
            data-variant-title="${this.escapeHtml(variant.title)}"
            ${!variant.available ? 'disabled' : ''}
          >
          <label
            for="${variantId}"
            class="variant-buttons__button btn btn--small btn--outline"
            data-variant-id="${variant.id}"
          >
            ${this.escapeHtml(variant.title)}
          </label>
        </div>
      `;
    });

    selectorHtml += `
        </div>
      </div>
    `;

    return selectorHtml;
  }

  /**
   * Render stock status indicators
   */
  renderStockStatus(product) {
    return `
      <div
        class="product-card__preferred-stock-status twp-stock-status text-sm mt-2 flex items-start gap-1"
        data-product-id="${product.id}"
        aria-live="polite"
        data-delivery-status
      >
        <span class="product__inventory-icon"></span>
        <span class="twp-status-text"></span>
      </div>
      <div
        class="product-card__preferred-stock-status twp-stock-status text-sm mt-2 flex items-start gap-1"
        data-product-id="${product.id}"
        data-click-and-collect-status
        aria-live="polite"
      >
        <span class="product__inventory-icon"></span>
        <span class="twp-status-text"></span>
      </div>
    `;
  }

  /**
   * Generate variants data for stock checking
   */
  generateVariantsData(variants) {
    return variants.map(variant => ({
      id: variant.id,
      stockData: variant.metafields?.pickup?.click_and_collect || null,
      shippingData: `"${variant.metafields?.shipping?.delivery?.quantities_available}"` || null,
    }));
  }

  /**
   * Calculate image aspect ratio
   */
  calculateImageRatio(featuredMedia) {
    if (this.settings.imageRatio === 'adapt' && featuredMedia) {
      return featuredMedia.aspect_ratio || 1;
    }
    return this.settings.imageRatio;
  }

  /**
   * Generate image URL with width parameter
   */
  generateImageUrl(baseUrl, width) {
    return `${baseUrl}?width=${width}`;
  }

  /**
   * Generate responsive srcset
   */
  generateSrcset(baseUrl, maxWidth) {
    const widths = this.settings.imageWidths.split(', ').map(w => parseInt(w.trim()));
    return widths
      .filter(width => width <= maxWidth)
      .map(width => `${baseUrl}&width=${width} ${width}w`)
      .join(', ');
  }

  /**
   * Generate sale badge text
   */
  generateSaleBadge(variant) {
    const { compare_at_price, price } = variant;

    switch (this.settings.saleBadgeType) {
      case 'show_percentage':
        const percentage = Math.round(((compare_at_price - price) / compare_at_price) * 100);
        return `-${percentage}%`;
      case 'show_amount':
        const amount = this.formatMoney(compare_at_price - price);
        return `Save ${amount}`;
      default:
        return 'On Sale';
    }
  }

  /**
   * Format money values
   */
  formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  /**
   * Render loading spinner
   */
  renderLoadingSpinner() {
    return `
      <div class="loading__spinner hidden">
        <svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>
    `;
  }

  /**
   * Escape HTML characters
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Escape JSON for safe HTML attribute output
   */
  escapeJsonForAttribute(jsonData) {
    const jsonString = typeof jsonData === 'string' ? jsonData : JSON.stringify(jsonData);
    return jsonString
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /**
   * Generate card wrapper classes
   */
  generateCardWrapperClasses(options) {
    let classes = '';
    if (options.customClass) classes += ` ${options.customClass}`;
    if (this.settings.mobileHideQuickAdd) classes += ' mobile-hide-quick-add';
    if (options.listOnMobile) classes += ' product-card-list-on-mobile';
    return classes;
  }

  /**
   * Render remaining placeholder methods
   */
  renderQuickViewButton(modalId) {
    if (!this.settings.showQuickView) return '';
    return `
      <button
        class="product-card__quickview btn btn--icon-circle ${this.settings.buttonStyle} no-js-hidden"
        aria-label="Quick view"
        aria-controls="${modalId}"
        aria-haspopup="dialog"
      >
        <span class="btn__text flex gap-1 items-center">
          <svg class="icon icon-search" viewBox="0 0 24 24"><path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" fill="none"/></svg>
        </span>
      </button>
    `;
  }

  renderQuickAddButton(product, variant, modalId, sectionId) {
    return ''; // Simplified for now
  }

  /**
   * Render compare checkbox
   */
  renderCompareCheckbox(product, sectionId) {
    if (!this.settings.showCompareCheckbox) return '';

    const checkboxId = `CompareCheckbox-${sectionId}-${product.id}`;

    return `
      <compare-checkbox class="product-card__compare hidden">
        <label
          data-tooltip="left"
          class="flex items-center justify-center cursor-pointer"
          for="${checkboxId}"
        >
          <input
            id="${checkboxId}"
            class="js-compare-checkbox"
            type="checkbox"
            name="product-compare"
            data-product-id="${product.id}"
            data-product-url="${product.url}"
          >
          <span class="tooltip pointer-events-none">Compare</span>
        </label>
      </compare-checkbox>
    `;
  }

  renderCustomTagBadges(tags) {
    return ''; // Simplified for now
  }

  renderListActions(product, variant, modalId) {
    return `
      <div class="product-card__list-actions hidden gap-3 flex-col">
        <a href="${product.url}" class="btn btn--secondary" data-product-link>
          <span class="btn__text flex gap-1 items-center">View details</span>
        </a>
      </div>
    `;
  }

  renderQuickViewModal(product, modalId) {
    return `
      <quick-view-modal id="${modalId}" class="quick-view drawer drawer--right drawer--loading" data-product-url="${
      product.url
    }" hidden>
        <div class="fixed-overlay" aria-controls="${modalId}"></div>
        <div class="drawer__inner">
          <button class="drawer__close-btn z-2 drawer__close-btn--fill-mobile" aria-controls="${modalId}" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-close icon--large icon--thick" fill="none" viewBox="0 0 24 24">
              <path d="M18.75 5.13496L5.25 18.8544" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
              <path d="M18.75 18.8544L5.25 5.13496" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </button>
          ${this.renderLoadingSpinner()}
          <div class="drawer__body v-scrollable h-full quick-view__content"></div>
        </div>
      </quick-view-modal>
    `;
  }
}

// Export for use
window.ProductCardRenderer = ProductCardRenderer;
